import type { PaginatedHasNextResponse } from '@shared/api-client/pagination';
import { isNonEmptyArray } from '@shared/utils/array';
import { toQueryParams } from '@shared/utils/url';
import {
  QueryKey,
  QueryStatus,
  useQuery,
  UseQueryOptions,
} from '@tanstack/react-query';
import { v2 } from '@/api/client';
import { ONE_HOUR } from '@/util/time';
import { useAccountSelector } from '../account/AccountProvider';
import criteriaBuilder from '../query/criteriaBuilder';
import { createQueryClause } from '../query/queryClause';

const defaultParams: ListParams<BusinessEntityInclude> = {
  size: 2000,
};

export function useBusinessEntityByIdQuery<T>(
  id: string | number | undefined,
  include?: BusinessEntityInclude,
  queryOptions: Partial<UseQueryOptions<T>> = {}
) {
  const { account } = useAccountSelector();

  return useQuery({
    queryKey: ['businessEntity', id, include],
    queryFn: () =>
      v2.get<T>(
        `entity/${id}${toQueryParams({
          query: applyAccountCriteria(account?.id),
          include,
        })}`
      ),
    enabled: Boolean(id),
    ...queryOptions,
  });
}

export function useBusinessEntityQuery<T extends BusinessEntityBase[]>(
  criteria?: BusinessEntityCriteria,
  params: ListParams<BusinessEntityInclude> = {},
  queryOptions: Partial<UseQueryOptions<T>> = {},
  fetchAll: boolean = true
) {
  const { account } = useAccountSelector();

  const p = {
    ...defaultParams,
    query: applyAccountCriteria(account?.id, criteria),
    ...params,
  };

  const queryKey: QueryKey = isNonEmptyArray(queryOptions.queryKey)
    ? [...queryOptions.queryKey, account]
    : ['businessEntity', p, fetchAll];

  const fetchEntities = async (
    page: number = 0,
    accumulatedResult?: T
  ): Promise<T> => {
    const response = await v2.get<PaginatedHasNextResponse<T>>(
      `entity${toQueryParams({ ...p, page })}`,
      { selector: (json) => json }
    );
    // @ts-expect-error
    const result: T = Array.isArray(accumulatedResult)
      ? accumulatedResult.concat(response.data)
      : response.data;

    return response.hasNext && fetchAll
      ? fetchEntities(++page, result)
      : Promise.resolve(result);
  };

  return useQuery({
    staleTime: ONE_HOUR,
    queryFn: () => fetchEntities(),
    ...queryOptions,
    queryKey,
  });
}

export function useAccountHierarchyQuery(id: string | undefined) {
  return useQuery({
    queryKey: ['accountHierarchy', id],
    queryFn: () =>
      v2.get<BusinessEntitySummary[]>(`entity/${id}/account_hierarchy`, {
        selector: (json) => json,
      }),
    enabled: Boolean(id),
  });
}

interface HasPermitResult {
  hasPermit: boolean;
  status: QueryStatus;
  isLoading: boolean;
}

export function useHasBusinessEntityPermit(
  id: string | undefined,
  permit: Permit
): HasPermitResult {
  const {
    data: entity,
    status,
    isLoading,
  } = useBusinessEntityByIdQuery<BusinessEntityBase>(id, ['permits']);
  const hasPermit = entity?.permits?.includes(permit) ?? false;

  return { hasPermit, status, isLoading };
}

function applyAccountCriteria(account?: string, criteria?: Criteria) {
  if (!account && !criteria) {
    return;
  }

  const builder = criteriaBuilder(criteria);

  // Prevent applying account in criteria when it already contains a more
  // specific ancestor criteria. This is due to that backend service uses OR
  // operator when providing multiple ancestors (and other types as well...).
  const hasAncestorCriteria = criteria?.ancestor !== undefined;

  if (account && !hasAncestorCriteria) {
    builder.ancestor(account);
  }

  return createQueryClause(builder.get());
}
